import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMAGE = "/hero.jpg";

const lineVariants = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const imageRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="accueil" data-testid="hero-section" className="relative overflow-hidden bg-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.045) 1px, transparent 1px)",
          backgroundSize: "clamp(48px, 8vw, 120px) 100%",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-36 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-16 lg:pb-28 lg:pt-44">
        <div className="lg:col-span-7">
          <motion.p
            custom={0.1}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            data-testid="hero-overline"
            className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-brand"
          >
            <span className="h-px w-10 bg-sun" aria-hidden="true" />
            Rénovation complète à Genève
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-light leading-[1.06] tracking-tight text-ink sm:text-6xl lg:text-7xl"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} initial="hidden" animate="visible" variants={lineVariants} className="block">
                Nous transformons
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                custom={1}
                initial="hidden"
                animate="visible"
                variants={lineVariants}
                className="block font-medium text-brand"
              >
                votre logement
              </motion.span>
            </span>
          </h1>

          <motion.div custom={0.7} initial="hidden" animate="visible" variants={fadeVariants}>
            <p data-testid="hero-description" className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              <strong className="font-semibold text-ink">Zion Solutions Suisse Sàrl</strong> réalise
              la rénovation complète de maisons et d’appartements à Genève et dans les environs.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Grâce à notre expérience, nous vous proposons des solutions adaptées à vos besoins, à
              vos préférences et à votre budget.
            </p>
          </motion.div>

          <motion.div
            custom={0.9}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <button
              type="button"
              onClick={() => scrollToId("devis")}
              data-testid="hero-cta-devis"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,color,transform] duration-200 hover:bg-sun hover:text-ink active:scale-[0.98]"
            >
              Demander un devis
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </button>
            <a
              href="tel:+41782135944"
              data-testid="hero-cta-call"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-ink transition-[border-color,color,background-color] duration-200 hover:border-brand hover:text-brand"
            >
              <Phone className="h-4 w-4" strokeWidth={1.8} />
              Nous appeler
            </a>
          </motion.div>

          <motion.p
            custom={1.05}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="mt-8 flex items-center gap-2 text-sm text-slate-500"
          >
            <MapPin className="h-4 w-4 text-brand" strokeWidth={1.8} />
            Genève et environs — devis sans engagement
          </motion.p>
        </div>

        <div className="lg:col-span-5" ref={imageRef}>
          <motion.figure
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-sm border border-slate-200 shadow-[0_24px_60px_rgba(15,23,42,0.10)]">
              <motion.img
                src={HERO_IMAGE}
                alt="Séjour lumineux d’un appartement rénové à Genève après une rénovation complète"
                data-testid="hero-image"
                style={{ y: parallaxY, scale: 1.12 }}
                className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]"
                loading="eager"
                fetchpriority="high"
              />
            </div>
            <figcaption className="absolute -bottom-5 left-5 rounded-sm border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.18em] text-slate-600 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
              Appartement rénové — Genève
            </figcaption>
            <div
              aria-hidden="true"
              className="absolute -right-4 -top-4 -z-10 h-full w-full rounded-sm border border-brand/20"
            />
          </motion.figure>
        </div>
      </div>
    </section>
  );
};
